import React, { useState, useRef, useCallback, useEffect } from "react";
import * as XLSX from "xlsx";
import { api } from "../api";

// April-based fiscal year: period 1 = April, period 10 = January
const periodToMonth: Record<number, string> = {
  1: "April", 2: "May", 3: "June", 4: "July",
  5: "August", 6: "September", 7: "October", 8: "November",
  9: "December", 10: "January", 11: "February", 12: "March",
};

const FISCAL_MONTHS = [
  "April","May","June","July","August","September",
  "October","November","December","January","February","March",
];

const fmtK = (v: number) => {
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(0)}K`;
  return v.toLocaleString("en-US", { maximumFractionDigits: 0 });
};
const fmtMoney = (v: number) => v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

// ── Month abbreviation → full name mapping for filename detection ────────────
const MONTH_ABBREVS: Record<string, string> = {
  jan: "January", feb: "February", mar: "March", apr: "April",
  may: "May", jun: "June", jul: "July", aug: "August",
  sep: "September", oct: "October", nov: "November", dec: "December",
  january: "January", february: "February", march: "March", april: "April",
  june: "June", july: "July", august: "August",
  september: "September", october: "October", november: "November", december: "December",
};

// ── Period detection from workbook sheet ──────────────────────────────────────
function detectPeriodFromSheet(wb: XLSX.WorkBook, sheetName: string): number | null {
  const sheet = wb.Sheets[sheetName];
  if (!sheet) return null;

  const raw = XLSX.utils.sheet_to_json(sheet, {
    header: 1, defval: "",
  }) as unknown[][];

  for (let row = 0; row <= 3; row++) {
    for (let col = 2; col <= 17; col++) {
      const v = Number(raw[row]?.[col]);
      if (!isNaN(v) && v >= 1 && v <= 12) return v;
    }
  }
  return null;
}

// ── Detect month from filename (e.g. "Apr FY26 Engineering..." → "April") ───
function detectMonthFromFilename(filename: string): string | null {
  const lower = filename.toLowerCase().replace(/[_\-\.]/g, " ");
  // Try to find month names/abbreviations as whole words
  const words = lower.split(/\s+/);
  for (const word of words) {
    if (MONTH_ABBREVS[word]) return MONTH_ABBREVS[word];
  }
  return null;
}

// ── Combined detection: try filename first, then each sheet ─────────────────
function detectMonth(wb: XLSX.WorkBook, filename: string): string | null {
  // 1. Try filename
  const fromFile = detectMonthFromFilename(filename);
  if (fromFile) return fromFile;

  // 2. Try each sheet for period number
  for (const name of wb.SheetNames) {
    const period = detectPeriodFromSheet(wb, name);
    if (period) return periodToMonth[period] || null;
  }
  return null;
}

// ── Types ────────────────────────────────────────────────────────────────────
interface UploadResult {
  ok: boolean;
  message?: string;
  details?: string[];
  error?: string;
  reconciliation?: {
    parsed_expense_rows: number;
    parsed_headcount_rows: number;
    parsed_cost_centers: number;
    skipped_rows: number;
    skipped_accounts: string[];
  };
}

interface UploadStatusEntry {
  month: string;
  cost_centers: string[];
  last_uploaded: string;
}

interface CCOption {
  cost_center_number: string;
  cost_center_name:   string;
}

interface MatrixRow {
  category: string;
  by_cc: Record<string, number>;
  total: number;
}

interface HCMatrixRow {
  type: string;
  by_cc: Record<string, number>;
  total: number;
}

interface ValidationResult {
  sheet_fiscal_year: string | null;
  sheet_summary: {
    cost_centers: number;
    unique_accounts: number;
    expense_rows: number;
    headcount_rows: number;
    grand_total: number;
  };
  cost_centers: { found: string[]; missing: string[] };
  accounts: { found_count: number; missing_count: number; missing: { cost_center: string; account: string; category: string }[] };
  cc_order: string[];
  matrix: MatrixRow[];
  cc_totals: Record<string, number>;
  headcount_matrix: HCMatrixRow[];
  grand_total: number;
  valid: boolean;
}

interface SheetInfo {
  name: string;
  rowCount: number;
  type: string; // "actuals" | "summary" | "other"
}

interface Props {
  fiscalYear: string;
  onUploadSuccess?: () => void;
  onBUChange?: (bu: string) => void;
}

const ActualsUploader: React.FC<Props> = ({ fiscalYear, onUploadSuccess, onBUChange }) => {
  const [file,            setFile]            = useState<File | null>(null);
  const [detectedMonth,   setDetectedMonth]   = useState<string | null>(null);
  const [selectedMonth,   setSelectedMonth]   = useState<string>("January");
  const [monthMismatch,   setMonthMismatch]   = useState(false);
  const [dragging,        setDragging]        = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [showReport,      setShowReport]      = useState(false);
  const [uploading,       setUploading]       = useState(false);
  const [result,          setResult]          = useState<UploadResult | null>(null);
  const [uploadStatus,    setUploadStatus]    = useState<UploadStatusEntry[]>([]);
  const [ccsByBU,         setCCsByBU]         = useState<Record<string, CCOption[]>>({});
  const [allBUs,          setAllBUs]          = useState<string[]>([]);
  const [selectedBU,      setSelectedBU]      = useState<string>("");

  // Sheet selection state
  const [sheets,          setSheets]          = useState<SheetInfo[]>([]);
  const [selectedSheet,   setSelectedSheet]   = useState("");
  const [previewLoading,  setPreviewLoading]  = useState(false);
  const [previewError,    setPreviewError]    = useState("");

  // Validation state
  const [validating,     setValidating]     = useState(false);
  const [validation,     setValidation]     = useState<ValidationResult | null>(null);
  const [validationErr,  setValidationErr]  = useState<string | null>(null);
  const [addingCCs,      setAddingCCs]      = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Load upload status on mount ─────────────────────────���────────────────
  const loadUploadStatus = useCallback(() => {
    return api.get(`/admin/actuals-upload-status?fy=${encodeURIComponent(fiscalYear)}`)
      .then(r => setUploadStatus(r.data.status || []))
      .catch(() => {});
  }, [fiscalYear]);

  useEffect(() => {
    loadUploadStatus();
    api.get("/admin/cost-centers-by-bu", { params: { fy: fiscalYear } })
      .then(r => { setCCsByBU(r.data.by_bu || {}); setAllBUs(r.data.business_units || []); })
      .catch(err => { console.error("Failed to load BUs:", err.response?.status, err.message); });
  }, [loadUploadStatus]);

  // ── Preview file: get sheet list from backend ─────────────────────────────
  const previewFile = useCallback(async (f: File) => {
    setPreviewLoading(true);
    setPreviewError("");
    setSheets([]);
    setSelectedSheet("");

    try {
      const formData = new FormData();
      formData.append("file", f);

      const res = await api.post("/upload-actuals/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const sheetList: SheetInfo[] = res.data.sheets;
      setSheets(sheetList);

      // Auto-select the first "actuals" type sheet, or fall back to first sheet
      const actualsMatch = sheetList.find(s => s.type === "actuals");
      setSelectedSheet(actualsMatch ? actualsMatch.name : sheetList[0]?.name || "");
    } catch (err: any) {
      setPreviewError(err.response?.data?.error || "Failed to read workbook.");
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  // ── File processing (shared by click + drag-drop) ────────────────────────
  const processFile = useCallback((f: File) => {
    setFile(f);
    setResult(null);
    setShowConfirm(false);
    setValidation(null);
    setValidationErr(null);
    setSheets([]);
    setSelectedSheet("");
    setPreviewError("");

    // Detect month from filename + workbook sheets
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const wb   = XLSX.read(data, { type: "array" });
        const detected = detectMonth(wb, f.name);

        if (detected) {
          setDetectedMonth(detected);
          setSelectedMonth(detected);
          setMonthMismatch(false);
        } else {
          setDetectedMonth(null);
        }
      } catch {
        setDetectedMonth(null);
      }
    };
    reader.readAsArrayBuffer(f);

    // Also preview sheets via backend
    previewFile(f);
  }, [previewFile]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) processFile(f);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f && (f.name.endsWith(".xlsx") || f.name.endsWith(".xls"))) processFile(f);
  };

  const handleMonthSelect = (month: string) => {
    setSelectedMonth(month);
    setMonthMismatch(detectedMonth !== null && detectedMonth !== month);
  };

  // CC numbers for the selected BU
  const buCCSet = selectedBU && ccsByBU[selectedBU]
    ? new Set(ccsByBU[selectedBU].map(c => c.cost_center_number))
    : null;

  // ── Already-uploaded cost centers for selected month (filtered by BU) ──
  const allExistingCCs = uploadStatus.find(s => s.month === selectedMonth)?.cost_centers ?? [];
  const existingCCs = buCCSet
    ? allExistingCCs.filter(cc => buCCSet.has(cc))
    : allExistingCCs;

  // ── Validate ───────────────────────────────────────────────────────────
  const handleValidate = async () => {
    if (!file) return;
    setValidating(true);
    setValidation(null);
    setValidationErr(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (selectedSheet) formData.append("sheet", selectedSheet);
      const res = await api.post("/upload-actuals/validate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setValidation(res.data);
    } catch (err: any) {
      setValidationErr(err.response?.data?.error || "Validation failed");
    } finally {
      setValidating(false);
    }
  };

  // ── Add missing CCs and re-validate ─────────────────────────────────────
  const handleAddMissingCCs = async () => {
    if (!validation || validation.cost_centers.missing.length === 0) return;
    setAddingCCs(true);
    try {
      await api.post("/upload-actuals/add-cost-centers", {
        cost_centers: validation.cost_centers.missing,
        business_unit: selectedBU || undefined,
        fiscal_year: fiscalYear,
        month: selectedMonth,
      });
      // Re-validate to refresh the results
      await handleValidate();
    } catch (err: any) {
      setValidationErr(err.response?.data?.error || "Failed to add cost centers");
    } finally {
      setAddingCCs(false);
    }
  };

  // ── Upload ───────────────────────────────────────────────────────────────
  const handleConfirmedUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("month", selectedMonth);
      formData.append("fiscal_year", fiscalYear);
      if (selectedSheet) formData.append("sheet", selectedSheet);

      const res = await api.post("/upload-actuals", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setResult({ ok: true, message: res.data.message, details: res.data.details, reconciliation: res.data.reconciliation });
      loadUploadStatus();
      onUploadSuccess?.();
    } catch (err: any) {
      const msg = err.response?.data?.error || "Upload failed. Check file and try again.";
      setResult({ ok: false, error: msg });
    } finally {
      setUploading(false);
      setShowConfirm(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setDetectedMonth(null);
    setMonthMismatch(false);
    setShowConfirm(false);
    setResult(null);
    setValidation(null);
    setValidationErr(null);
    setSheets([]);
    setSelectedSheet("");
    setPreviewError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="uploader">

      {/* ── BU filter ────────────────────────────────────────────────────── */}
      {allBUs.length > 0 && (
        <div className="uploader-field">
          <label className="uploader-field-label">Business Unit <span className="uploader-required">*</span></label>
          <select
            className="uploader-select"
            value={selectedBU}
            onChange={e => { setSelectedBU(e.target.value); onBUChange?.(e.target.value); }}
          >
            <option value="">Select a business unit…</option>
            {allBUs.map(bu => <option key={bu} value={bu}>{bu}</option>)}
          </select>
        </div>
      )}

      {/* ── Grayed-out wrapper until BU is selected ────────────────────── */}
      <div style={!selectedBU ? { opacity: 0.45, pointerEvents: "none" } : undefined}>

      {/* ── Instruction note ──────────────────────────────────────────────── */}
      <div className="uploader-instruction-note">
        <span className="material-icons">info</span>
        <span>Import one BU at a time. Remove all columns except actuals data before uploading.</span>
      </div>

      {/* ── Month selector ───────────────────────────────────────────────── */}
      <div className="uploader-month-section">
        <div className="uploader-month-label">
          Actuals month:
          {monthMismatch && (
            <span className="uploader-mismatch-badge">
              <span className="material-icons">warning</span>
              File indicates {detectedMonth} — verify selection
            </span>
          )}
          {!monthMismatch && detectedMonth && file && (
            <span className="uploader-match-badge">
              <span className="material-icons">check_circle</span>
              Detected: {detectedMonth}
            </span>
          )}
        </div>
        <div className="uploader-month-pills">
          {FISCAL_MONTHS.map(m => {
            const statusEntry = uploadStatus.find(s => s.month === m);
            const hasData = statusEntry
              ? buCCSet
                ? statusEntry.cost_centers.some(cc => buCCSet.has(cc))
                : true
              : false;
            return (
              <button
                key={m}
                className={`uploader-month-pill${selectedMonth === m ? " uploader-month-pill--active" : ""}${detectedMonth === m && selectedMonth !== m ? " uploader-month-pill--detected" : ""}${hasData ? " uploader-month-pill--has-data" : ""}`}
                onClick={() => handleMonthSelect(m)}
                title={hasData ? `Data already uploaded for ${m}` : undefined}
              >
                {m.slice(0, 3)}
                {hasData && <span className="uploader-month-dot" />}
              </button>
            );
          })}
        </div>
        {existingCCs.length > 0 && file && !result && !validation && !showConfirm && (
          <div className="uploader-early-warning">
            <span className="material-icons">info</span>
            <span>Data already exists for {selectedMonth} ({existingCCs.length} cost centers). Uploading will overwrite.</span>
          </div>
        )}
      </div>

      {/* ── Section divider ─────────────────────────────────────────────── */}
      <div className="uploader-section-divider" />
      <div className="uploader-section-label">File Upload</div>

      {/* ── Drop zone ────────────────────────────────────────────────────── */}
      <div
        className={`uploader-dropzone${dragging ? " uploader-dropzone--over" : ""}${file ? " uploader-dropzone--has-file" : ""}`}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !file && fileInputRef.current?.click()}
        aria-label="Upload file"
        role="button"
        tabIndex={0}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleFileInput}
        />
        {file ? (
          <div className="uploader-file-info">
            <span className="material-icons uploader-file-icon">description</span>
            <div>
              <div className="uploader-file-name">{file.name}</div>
              <div className="uploader-file-size">
                {(file.size / 1024).toFixed(0)} KB
                {detectedMonth && (
                  <span className="uploader-detected-badge">
                    Period detected: {detectedMonth}
                  </span>
                )}
              </div>
            </div>
            <button className="uploader-clear" onClick={e => { e.stopPropagation(); clearFile(); }}>
              <span className="material-icons">close</span>
            </button>
          </div>
        ) : (
          <div className="uploader-prompt">
            <span className="material-icons uploader-prompt-icon">upload_file</span>
            <span className="uploader-prompt-main">Drop .xlsx here or <span className="uploader-link">browse</span></span>
            <span className="uploader-prompt-sub">Accepts .xlsx — monthly BU actuals report</span>
          </div>
        )}
      </div>

      {/* ── Preview loading ──────────────────────────────────────────────── */}
      {previewLoading && (
        <div style={{ padding: "12px 0", color: "#6b7280", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <span className="material-icons" style={{ fontSize: 16, animation: "fv-spin 0.8s linear infinite" }}>sync</span>
          Reading workbook sheets…
        </div>
      )}

      {/* ── Preview error ────────────────────────────────────────────────── */}
      {previewError && (
        <div className="uploader-result uploader-result--err" style={{ marginTop: 8 }}>
          <span className="material-icons">error</span>
          <div><div className="uploader-result-msg">{previewError}</div></div>
        </div>
      )}

      {/* ── Sheet selection ──────────────────────────────────────────────── */}
      {file && sheets.length > 0 && !result && (
        <div style={{
          marginTop: 12, padding: 16, background: "#f8fafc",
          border: "1px solid #e2e8f0", borderRadius: 8,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b", marginBottom: 4 }}>
            Select Actuals Sheet
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>
            {sheets.length} sheet{sheets.length !== 1 ? "s" : ""} found. Choose which sheet contains the actuals data.
          </div>

          {/* Available sheets list */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3af", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
              Sheets in Workbook
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {sheets.map(s => {
                const badgeColors: Record<string, { bg: string; fg: string; label: string }> = {
                  actuals: { bg: "#dbeafe", fg: "#1d4ed8", label: "Actuals" },
                  summary: { bg: "#fef9c3", fg: "#854d0e", label: "Summary" },
                  other:   { bg: "#f1f5f9", fg: "#64748b", label: "Other" },
                };
                const c = badgeColors[s.type] || badgeColors.other;
                return (
                  <div key={s.name} style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "4px 10px", background: "#fff", border: "1px solid #e2e8f0",
                    borderRadius: 6, fontSize: 12, color: "#374151",
                  }}>
                    <span style={{
                      display: "inline-block", padding: "2px 8px", borderRadius: 4,
                      fontSize: 11, fontWeight: 600, background: c.bg, color: c.fg,
                    }}>{c.label}</span>
                    <span>{s.name}</span>
                    <span style={{ color: "#9ca3af", fontSize: 11 }}>{s.rowCount} rows</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sheet dropdown */}
          <div className="uploader-field" style={{ marginBottom: 0 }}>
            <label className="uploader-field-label" style={{ marginBottom: 2 }}>
              Actuals Sheet <span className="uploader-required">*</span>
            </label>
            <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>
              Sheet with TOTEXP/TOTHEADCOUNT rows and cost center columns
            </div>
            <select
              className="uploader-select"
              value={selectedSheet}
              onChange={e => { setSelectedSheet(e.target.value); setValidation(null); setValidationErr(null); }}
            >
              <option value="">Select actuals sheet…</option>
              {sheets.map(s => (
                <option key={s.name} value={s.name}>{s.name} ({s.rowCount} rows){s.type === "actuals" ? " ★" : ""}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* ── Validate button ──────────────────────────────────────────────── */}
      {file && selectedSheet && !validation && !showConfirm && !result && (
        <button
          className="uploader-submit uploader-submit--validate"
          onClick={handleValidate}
          disabled={validating || !selectedBU}
        >
          <span className="material-icons">{validating ? "sync" : "fact_check"}</span>
          {validating ? "Validating…" : "Validate Sheet"}
        </button>
      )}

      {validationErr && (
        <div className="uploader-result uploader-result--err">
          <span className="material-icons">error</span>
          <div><div className="uploader-result-msg">{validationErr}</div></div>
        </div>
      )}

      {/* ── Validation results ───────────────────────────────────────────── */}
      {validation && !showConfirm && !result && (
        <div className="uploader-validation">
          <div className="uploader-validation-header">
            <span className="material-icons" style={{ color: validation.valid ? "#16a34a" : "#dc2626" }}>
              {validation.valid ? "check_circle" : "warning"}
            </span>
            <span className="uploader-validation-title">
              {validation.valid ? "Validation Passed" : "Validation — Issues Found"}
            </span>
          </div>

          {/* FY check */}
          {validation.sheet_fiscal_year && validation.sheet_fiscal_year !== fiscalYear && (
            <div className="uploader-early-warning" style={{ marginBottom: 12 }}>
              <span className="material-icons">warning</span>
              <span>Sheet contains <strong>{validation.sheet_fiscal_year}</strong> but you are uploading to <strong>{fiscalYear}</strong>. Please verify this is correct.</span>
            </div>
          )}
          {validation.sheet_fiscal_year && validation.sheet_fiscal_year === fiscalYear && (
            <div className="uploader-val-section-header" style={{ color: "#16a34a", marginBottom: 8 }}>
              <span className="material-icons" style={{ fontSize: "16px" }}>check_circle_outline</span>
              Fiscal Year: Sheet matches {fiscalYear}
            </div>
          )}
          {!validation.sheet_fiscal_year && (
            <div className="uploader-early-warning" style={{ marginBottom: 12 }}>
              <span className="material-icons">info</span>
              <span>Could not detect fiscal year from sheet. Uploading to <strong>{fiscalYear}</strong> — please verify this is correct.</span>
            </div>
          )}

          {/* Summary */}
          <div className="uploader-validation-summary">
            <div className="uploader-val-stat">
              <span className="uploader-val-stat-num">{validation.sheet_summary.cost_centers}</span>
              <span className="uploader-val-stat-label">Cost Centers</span>
            </div>
            <div className="uploader-val-stat">
              <span className="uploader-val-stat-num">{validation.sheet_summary.expense_rows}</span>
              <span className="uploader-val-stat-label">Expense Rows</span>
            </div>
            <div className="uploader-val-stat">
              <span className="uploader-val-stat-num">{validation.sheet_summary.headcount_rows}</span>
              <span className="uploader-val-stat-label">Headcount Rows</span>
            </div>
          </div>

          {/* Cost Centers */}
          <div className="uploader-val-section">
            <div className="uploader-val-section-header">
              <span className="material-icons" style={{ fontSize: "16px", color: validation.cost_centers.missing.length > 0 ? "#dc2626" : "#16a34a" }}>
                {validation.cost_centers.missing.length > 0 ? "error_outline" : "check_circle_outline"}
              </span>
              Cost Centers: {validation.cost_centers.found.length} in database
              {validation.cost_centers.missing.length > 0 && (
                <span style={{ color: "#dc2626", fontWeight: 600 }}> · {validation.cost_centers.missing.length} not in database</span>
              )}
            </div>
            {validation.cost_centers.missing.length > 0 && (
              <div className="uploader-val-missing">
                <div className="uploader-val-missing-label">
                  These cost centers are in the sheet but not registered in the database:
                </div>
                <div className="uploader-val-missing-list">
                  {validation.cost_centers.missing.map(cc => (
                    <span key={cc} className="uploader-val-missing-chip">{cc}</span>
                  ))}
                </div>
                <button
                  className="uploader-submit uploader-submit--add-cc"
                  onClick={handleAddMissingCCs}
                  disabled={addingCCs}
                  style={{ marginTop: 8 }}
                >
                  {addingCCs ? (
                    <><span className="uploader-spinner" />Adding…</>
                  ) : (
                    <><span className="material-icons">add_circle</span>Add {validation.cost_centers.missing.length} cost center{validation.cost_centers.missing.length > 1 ? "s" : ""} to database</>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Accounts */}
          <div className="uploader-val-section">
            <div className="uploader-val-section-header">
              <span className="material-icons" style={{ fontSize: "16px", color: validation.accounts.missing_count > 0 ? "#2563eb" : "#16a34a" }}>
                {validation.accounts.missing_count > 0 ? "info_outline" : "check_circle_outline"}
              </span>
              Accounts: {validation.accounts.found_count} of {validation.accounts.found_count + validation.accounts.missing_count} in database
              {validation.accounts.missing_count > 0 && (
                <span style={{ color: "#2563eb", fontWeight: 500 }}> · {validation.accounts.missing_count} new (will be auto-created on upload)</span>
              )}
            </div>
            {validation.accounts.missing_count > 0 && validation.accounts.missing.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: "#1e40af", marginBottom: 8,
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <span className="material-icons" style={{ fontSize: 16 }}>add_circle</span>
                  {validation.accounts.missing_count} New Account{validation.accounts.missing_count !== 1 ? "s" : ""} to Create
                </div>
                <div style={{
                  border: "1px solid #bfdbfe", borderRadius: 8, overflow: "hidden",
                  maxHeight: 200, overflowY: "auto",
                }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#eff6ff" }}>
                        <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, color: "#1e40af", width: 80 }}>Cost Center</th>
                        <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, color: "#1e40af" }}>Account</th>
                        <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600, color: "#1e40af", width: 140 }}>Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validation.accounts.missing.map((a, i) => (
                        <tr key={i} style={{ borderTop: "1px solid #dbeafe", background: i % 2 === 0 ? "#f8fbff" : "#fff" }}>
                          <td style={{ padding: "5px 10px", fontFamily: "monospace", color: "#1e40af" }}>{a.cost_center}</td>
                          <td style={{ padding: "5px 10px", color: "#1e293b" }}>{a.account}</td>
                          <td style={{ padding: "5px 10px", color: "#6b7280" }}>{a.category}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {validation.accounts.missing_count > validation.accounts.missing.length && (
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4, fontStyle: "italic" }}>
                    Showing {validation.accounts.missing.length} of {validation.accounts.missing_count} new accounts
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="uploader-validation-actions">
            <button
              className="uploader-cancel"
              onClick={() => { setValidation(null); }}
            >
              Re-validate
            </button>
            <button
              className="uploader-submit uploader-confirm-ok"
              onClick={() => setShowReport(true)}
            >
              <span className="material-icons">assessment</span>
              Review Totals
            </button>
          </div>
        </div>
      )}

      {/* ── Confirm step / Upload button ─────────────────────────────────── */}
      {showConfirm && (
        <div className="uploader-confirm">
          <div className="uploader-confirm-header">Confirm Import</div>
          <div className="uploader-confirm-body">
            <div className="uploader-confirm-row">
              <span className="uploader-confirm-label">Financial Year</span>
              <span className="uploader-confirm-value">{fiscalYear}</span>
            </div>
            <div className="uploader-confirm-row">
              <span className="uploader-confirm-label">Month</span>
              <span className="uploader-confirm-value">{selectedMonth}</span>
            </div>
            <div className="uploader-confirm-row">
              <span className="uploader-confirm-label">File</span>
              <span className="uploader-confirm-value">{file?.name}</span>
            </div>
            {selectedSheet && (
              <div className="uploader-confirm-row">
                <span className="uploader-confirm-label">Sheet</span>
                <span className="uploader-confirm-value">{selectedSheet}</span>
              </div>
            )}
            {validation && (
              <>
                <div className="uploader-confirm-row">
                  <span className="uploader-confirm-label">Expense Rows</span>
                  <span className="uploader-confirm-value">{validation.sheet_summary.expense_rows}</span>
                </div>
                <div className="uploader-confirm-row">
                  <span className="uploader-confirm-label">Cost Centers</span>
                  <span className="uploader-confirm-value">{validation.sheet_summary.cost_centers}</span>
                </div>
              </>
            )}
            {existingCCs.length > 0 && (
              <div className="uploader-overwrite-warning">
                <span className="material-icons">warning</span>
                <div>
                  <div className="uploader-overwrite-title">
                    Overwrite existing data for {selectedMonth}?
                  </div>
                  <div className="uploader-overwrite-ccs">
                    {existingCCs.join(", ")}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="uploader-confirm-actions">
            <button className="uploader-cancel" onClick={() => setShowConfirm(false)}>
              Cancel
            </button>
            <button
              className={`uploader-submit uploader-confirm-ok${uploading ? " uploader-submit--loading" : ""}`}
              onClick={handleConfirmedUpload}
              disabled={uploading}
            >
              {uploading ? (
                <><span className="uploader-spinner" />Uploading…</>
              ) : (
                <><span className="material-icons">cloud_upload</span>Confirm Upload</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── Result ───────────────────────────────────────────────────────── */}
      {result && (
        <div className={`uploader-result${result.ok ? " uploader-result--ok" : " uploader-result--err"}`}>
          <span className="material-icons">
            {result.ok ? "check_circle" : "error"}
          </span>
          <div>
            <div className="uploader-result-msg">{result.ok ? result.message : result.error}</div>
            {result.details?.map((d, i) => (
              <div key={i} className="uploader-result-detail">{d}</div>
            ))}
            {result.reconciliation && (
              <div className="uploader-reconciliation">
                <div className="uploader-result-detail">
                  Parsed: {result.reconciliation.parsed_expense_rows} expense rows · {result.reconciliation.parsed_headcount_rows} headcount rows · {result.reconciliation.parsed_cost_centers} cost centers
                </div>
                {result.reconciliation.skipped_rows > 0 && (
                  <div className="uploader-result-detail" style={{ color: "#dc2626" }}>
                    Skipped: {result.reconciliation.skipped_rows} rows (account lookup failed)
                    {result.reconciliation.skipped_accounts.length > 0 && (
                      <span> — {result.reconciliation.skipped_accounts.join(", ")}</span>
                    )}
                  </div>
                )}
                {result.reconciliation.skipped_rows === 0 && (
                  <div className="uploader-result-detail" style={{ color: "#16a34a" }}>
                    All rows written successfully — no data skipped
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Validation Report Modal ──────────────────────────────────────── */}
      {showReport && validation && (
        <div className="val-report-overlay" onClick={() => setShowReport(false)}>
          <div className="val-report-modal" onClick={e => e.stopPropagation()}>
            <div className="val-report-header">
              <span className="material-icons">assessment</span>
              <span className="val-report-title">Validation Report — {selectedMonth} {fiscalYear}</span>
              <button className="val-report-close" onClick={() => setShowReport(false)}>
                <span className="material-icons">close</span>
              </button>
            </div>

            <div className="val-report-body">
              {/* Expense matrix: categories × cost centers */}
              <div className="val-report-section">
                <div className="val-report-section-title">Expenses by Category × Cost Center</div>
                <div className="val-report-matrix-scroll">
                  <table className="val-report-table val-report-matrix">
                    <thead>
                      <tr>
                        <th className="val-matrix-cat">Category</th>
                        {validation.cc_order.map(cc => (
                          <th key={cc} className="val-matrix-cc">{cc}</th>
                        ))}
                        <th className="val-matrix-total">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validation.matrix.map(row => (
                        <tr key={row.category}>
                          <td className="val-matrix-cat">{row.category}</td>
                          {validation.cc_order.map(cc => (
                            <td key={cc} className="val-matrix-cc">
                              {row.by_cc[cc] ? fmtK(row.by_cc[cc]) : "—"}
                            </td>
                          ))}
                          <td className="val-matrix-total">{fmtMoney(row.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="val-matrix-footer">
                        <td className="val-matrix-cat"><strong>Total</strong></td>
                        {validation.cc_order.map(cc => (
                          <td key={cc} className="val-matrix-cc">
                            <strong>{validation.cc_totals[cc] ? fmtK(validation.cc_totals[cc]) : "—"}</strong>
                          </td>
                        ))}
                        <td className="val-matrix-total"><strong>{fmtMoney(validation.grand_total)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Headcount matrix */}
              {validation.headcount_matrix && validation.headcount_matrix.length > 0 && (
                <div className="val-report-section">
                  <div className="val-report-section-title">Headcount</div>
                  <div className="val-report-matrix-scroll">
                    <table className="val-report-table val-report-matrix">
                      <thead>
                        <tr>
                          <th className="val-matrix-cat">Type</th>
                          {validation.cc_order.map(cc => (
                            <th key={cc} className="val-matrix-cc">{cc}</th>
                          ))}
                          <th className="val-matrix-total">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validation.headcount_matrix.map(row => (
                          <tr key={row.type}>
                            <td className="val-matrix-cat">{row.type}</td>
                            {validation.cc_order.map(cc => (
                              <td key={cc} className="val-matrix-cc">
                                {row.by_cc[cc] ? row.by_cc[cc].toLocaleString("en-US", { maximumFractionDigits: 1 }) : "—"}
                              </td>
                            ))}
                            <td className="val-matrix-total">{row.total.toLocaleString("en-US", { maximumFractionDigits: 1 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Grand total highlight */}
              <div className="val-report-grand">
                <span className="val-report-grand-label">Grand Total</span>
                <span className="val-report-grand-value">{fmtMoney(validation.grand_total)}</span>
              </div>
            </div>

            <div className="val-report-footer">
              <button className="uploader-cancel" onClick={() => setShowReport(false)}>
                Back
              </button>
              <button
                className="uploader-submit uploader-confirm-ok"
                onClick={async () => { setShowReport(false); await loadUploadStatus(); setShowConfirm(true); }}
              >
                <span className="material-icons">cloud_upload</span>
                Proceed to Upload
              </button>
            </div>
          </div>
        </div>
      )}
      </div>{/* end grayed-out wrapper */}
    </div>
  );
};

export default ActualsUploader;
